import { useCallback } from 'react';
import { adminApi, subscriptionsApi } from '../../api/client';
import type { User, UpdateUserRequest } from '../../schemas/validation';
import type { AdminDashboardState } from './useAdminDashboardState';

// Open the user-facing dashboard with Roary auto-launching, after the admin
// has chosen who to build for. We swap tokens here (rather than calling
// useAuth.startMimicSession) because that helper hard-reloads the current
// URL — we want a redirect instead so Dashboard sees ?roary=open on mount.
async function mimicAndOpenRoary(userId: string): Promise<void> {
  const currentToken = localStorage.getItem('authToken');
  if (!currentToken) throw new Error('No authentication token found');

  const response = await adminApi.startMimicSession(userId);
  localStorage.setItem('originalAdminToken', currentToken);
  localStorage.setItem('mimickedUser', JSON.stringify(response.mimickedUser));
  localStorage.setItem('authToken', response.token);

  // Full navigation (not SPA push) so the auth state re-bootstraps cleanly.
  window.location.href = '/dashboard?roary=open';
}

interface UseAdminDashboardHandlersProps {
  state: AdminDashboardState;
  currentUser: User | null;
  startMimicSession: (userId: string) => Promise<void>;
}

export const useAdminDashboardHandlers = ({
  state,
  currentUser: _currentUser,
  startMimicSession,
}: UseAdminDashboardHandlersProps) => {
  const loadUsers = useCallback(async () => {
    try {
      state.setIsLoading(true);
      const usersData = await adminApi.getAllUsers();
      state.setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      state.setIsLoading(false);
    }
  }, [state]);

  const loadSubscriptionStats = useCallback(async () => {
    try {
      const plans = await subscriptionsApi.getPlans();
      state.setSubscriptionStats({ plans });
    } catch (error) {
      console.error('Failed to load subscription stats:', error);
    }
  }, [state]);

  const resetForm = useCallback(() => {
    state.setFormData({
      name: '',
      email: '',
      password: '',
      isAdmin: false,
      emailVerified: false,
    });
    state.setShowCreateForm(false);
    state.setEditingUser(null);
  }, [state]);

  const handleCreateUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!state.formData.name || !state.formData.email || !state.formData.password) return;

      try {
        state.setIsSubmitting(true);
        const newUser = await adminApi.createUser(state.formData);
        state.setUsers(prev => [...prev, newUser]);
        resetForm();
      } catch (error: any) {
        console.error('Failed to create user:', error);
      } finally {
        state.setIsSubmitting(false);
      }
    },
    [state, resetForm]
  );

  const handleUpdateUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!state.editingUser || !state.formData.name || !state.formData.email) return;

      try {
        state.setIsSubmitting(true);
        const updateData: UpdateUserRequest = {
          name: state.formData.name,
          email: state.formData.email,
          isAdmin: state.formData.isAdmin,
        };

        if (state.formData.password) {
          updateData.password = state.formData.password;
        }

        const updatedUser = await adminApi.updateUser(state.editingUser.id, updateData);
        state.setUsers(prev => prev.map(u => (u.id === state.editingUser!.id ? updatedUser : u)));
        resetForm();
      } catch (error: any) {
        console.error('Failed to update user:', error);
      } finally {
        state.setIsSubmitting(false);
      }
    },
    [state, resetForm]
  );

  const handleDeleteUser = useCallback(
    async (password: string) => {
      if (!state.userToDelete) return;

      try {
        state.setIsSubmitting(true);
        await adminApi.deleteUser(state.userToDelete.id, password);
        state.setUsers(prev => prev.filter(u => u.id !== state.userToDelete!.id));
        state.setShowDeleteModal(false);
        state.setUserToDelete(null);
      } catch (error: any) {
        console.error('Failed to delete user:', error);
        // Re-throw to let the modal handle the error display
        throw new Error(error.response?.data?.message || error.message || 'Failed to delete user');
      } finally {
        state.setIsSubmitting(false);
      }
    },
    [state]
  );

  const startToggleAdmin = useCallback(
    (user: User) => {
      state.setUserToToggleAdmin(user);
      state.setShowToggleAdminModal(true);
    },
    [state]
  );

  const handleToggleAdmin = useCallback(
    async (password: string) => {
      if (!state.userToToggleAdmin) return;

      try {
        state.setIsSubmitting(true);
        const updatedUser = await adminApi.toggleAdminStatus(state.userToToggleAdmin.id, password);
        state.setUsers(prev =>
          prev.map(u => (u.id === state.userToToggleAdmin!.id ? updatedUser : u))
        );
        state.setShowToggleAdminModal(false);
        state.setUserToToggleAdmin(null);
      } catch (error: any) {
        console.error('Failed to toggle admin status:', error);
        // Re-throw to let the modal handle the error display
        throw new Error(
          error.response?.data?.message || error.message || 'Failed to update admin status'
        );
      } finally {
        state.setIsSubmitting(false);
      }
    },
    [state]
  );

  const startEdit = useCallback(
    (user: User) => {
      state.setEditingUser(user);
      state.setFormData({
        name: user.name,
        email: user.email,
        password: '',
        isAdmin: user.isAdmin,
        emailVerified: false,
      });
      state.setShowCreateForm(true);
    },
    [state]
  );

  const startDelete = useCallback(
    (user: User) => {
      state.setUserToDelete(user);
      state.setShowDeleteModal(true);
    },
    [state]
  );

  const handleMimicUser = useCallback(
    async (user: User) => {
      try {
        state.setMimicingUserId(user.id);
        await startMimicSession(user.id);
      } catch (error: any) {
        console.error('Failed to mimic user:', error);
        alert(error.message || 'Failed to start mimic session');
        state.setMimicingUserId(null);
      }
    },
    [state, startMimicSession]
  );

  const handleBuildRoaryForUser = useCallback(
    async (user: User) => {
      try {
        state.setMimicingUserId(user.id);
        await mimicAndOpenRoary(user.id);
      } catch (error: any) {
        console.error('Failed to start Roary as user:', error);
        alert(error.message || 'Failed to start Roary session');
        state.setMimicingUserId(null);
      }
    },
    [state]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      state.setCurrentPage(page);
    },
    [state]
  );

  const handlePreviousPage = useCallback(() => {
    state.setCurrentPage(Math.max(state.currentPage - 1, 1));
  }, [state]);

  const handleNextPage = useCallback(
    (totalPages: number) => {
      state.setCurrentPage(Math.min(state.currentPage + 1, totalPages));
    },
    [state]
  );

  return {
    loadUsers,
    loadSubscriptionStats,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    startToggleAdmin,
    handleToggleAdmin,
    resetForm,
    startEdit,
    startDelete,
    handleMimicUser,
    handleBuildRoaryForUser,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
  };
};
